// src/pages/AdaptivePractice.js
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchPerformance } from "../api";
import RingChart from "../components/RingChart";

export default function AdaptivePractice() {
  const navigate = useNavigate();
  const [skills,  setSkills]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");

  useEffect(() => {
    fetchPerformance()
      .then(d  => { setSkills(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, []);

  if (loading) return <div className="loading"><div className="spinner" /><span>Loading practice areas…</span></div>;

  const weak  = skills.filter(s => s.level === "weak"    || s.accuracy < 0.40);
  const ok    = skills.filter(s => s.level !== "weak"    && s.accuracy >= 0.40);

  function startPractice(topic) {
     sessionStorage.removeItem("tempQuestions");
    sessionStorage.setItem("practiceConfig", JSON.stringify({ topic }));
    navigate("/practice/session");
  }

  function PracticeRow({ s }) {
    const pct  = Math.round(s.accuracy * 100);
    const diff = s.difficulty || "easy";
    const barC = pct >= 75 ? "var(--green)" : pct >= 50 ? "var(--amber)" : "var(--red)";
    const tagCls = diff === "easy" ? "tag-easy" : diff === "medium" ? "tag-medium" : "tag-hard";

    return (
      <div className="practice-row">
        <RingChart pct={pct} size={56} strokeWidth={5} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 3 }}>{s.topic}</div>
          <div style={{ fontSize: 12, color: "var(--text-secondary)", display: "flex", alignItems: "center", gap: 6 }}>
            {s.subject} &nbsp;·&nbsp; will get <span className={`tag ${tagCls}`}>{diff}</span> questions
          </div>
          <div style={{ height: 3, background: "var(--bg)", borderRadius: 2, width: 100, marginTop: 7, overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${pct}%`, background: barC, borderRadius: 2 }} />
          </div>
        </div>
        <button
          className="btn btn-sm"
          style={{ whiteSpace: "nowrap" }}
          onClick={() => startPractice(s.topic)}
        >
          Practice →
        </button>
      </div>
    );
  }

  return (
    <div>
      <h1 className="page-title">Adaptive practice</h1>
      <p className="page-sub">Difficulty auto-adjusts based on your accuracy per topic</p>

      {error && <div className="error-box">{error}</div>}

      {/* Engine rules banner */}
      <div className="engine-banner">
        ⚡&nbsp; Engine rules: accuracy &lt;40% → easy &nbsp;·&nbsp; 40–70% → medium &nbsp;·&nbsp; &gt;70% → hard
      </div>

      {/* Needs attention */}
      {weak.length > 0 && (
        <>
          <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: ".07em", marginBottom: 10 }}>
            Needs attention
          </div>
          {weak.map((s, i) => <PracticeRow key={i} s={s} />)}
        </>
      )}

      {/* Going well */}
      {ok.length > 0 && (
        <>
          <div style={{ fontSize: 10, fontWeight: 700, color: "var(--green)", textTransform: "uppercase", letterSpacing: ".07em", margin: "18px 0 10px" }}>
            Going well
          </div>
          {ok.map((s, i) => <PracticeRow key={i} s={s} />)}
        </>
      )}

      {/* Empty state */}
      {skills.length === 0 && (
        <div className="card" style={{ textAlign: "center", padding: "40px 20px" }}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>✏️</div>
          <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 14 }}>
            No performance data yet. Take a test first to unlock adaptive practice!
          </p>
          <button className="btn btn-dark btn-sm" onClick={() => navigate("/test")}>Take a test</button>
        </div>
      )}

      {/* Quick start — all topics from question bank */}
      <div style={{ marginTop: 24 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: ".07em", marginBottom: 10 }}>
          Quick start — all topics
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {["Pipes & Cisterns","Probability","Percentage","Coding-Decoding","Number Series","Syllogism","Grammar","Vocabulary","Banking Awareness"].map(t => (
            <button key={t} className="btn btn-sm" onClick={() => startPractice(t)}>{t}</button>
          ))}
        </div>
      </div>
    </div>
  );
}
